import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Lock, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminLogin() {
  const { admin, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!authLoading && admin) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid grid-cols-1 md:grid-cols-2" data-testid="admin-login-page">
      {/* LEFT */}
      <div className="relative hidden md:flex flex-col justify-between p-12 bg-[#050505] border-r border-white/10 overflow-hidden">
        <div className="absolute inset-0 topo-grid opacity-50" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-14 overflow-hidden flex items-start justify-center shrink-0">
              <img src="https://customer-assets.emergentagent.com/job_hybrid-dev-2/artifacts/2s3fmr01_Abstract%20Ridgeline%20with%20Circuit%20Line%20Motifs%283%29.png" alt="Ridgeline" className="w-14 h-14 object-contain" style={{ objectPosition: 'top', transform: 'scale(1.35)', transformOrigin: 'center 30%' }} />
            </div>
            <span className="font-display font-black tracking-tight text-white text-xl">Ridgec<span className="text-[#ff3333]">X</span></span>
          </Link>
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative">
          <p className="overline">Control Room</p>
          <h2 className="mt-4 font-display text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
            Secured access.<br/>
            <span className="text-[#ff3333]">Senior eyes only.</span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
            This console governs inbound inquiries and client engagements. All actions are audited.
          </p>
          <div className="mt-10 mono text-xs text-zinc-600 border-t border-white/10 pt-5">
            <div>SESSION · ENCRYPTED</div>
            <div className="mt-1">AUDIT LOG · ACTIVE</div>
            <div className="mt-1">MFA · COMING SOON</div>
          </div>
        </motion.div>
      </div>

      {/* RIGHT */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-[#0a0a0a]">
        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="w-full max-w-md border border-white/10 bg-[#111] p-8 md:p-10"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3 text-[#ff3333]">
            <Lock size={18}/> <span className="overline text-[#ff3333]">Admin Login</span>
          </div>
          <h1 className="mt-4 font-display text-3xl font-black text-white tracking-tight">Authenticate</h1>
          <p className="mt-2 text-sm text-zinc-400">Enter your credentials to access the control room.</p>

          <div className="mt-8 space-y-5">
            <label className="block">
              <span className="overline block mb-2">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ridge-input"
                placeholder="you@ridgecx.com"
                data-testid="admin-login-input-email"
              />
            </label>
            <label className="block">
              <span className="overline block mb-2">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="ridge-input"
                placeholder="••••••••"
                data-testid="admin-login-input-password"
              />
            </label>
          </div>

          {error && (
            <div className="mt-5 border border-red-500/30 bg-red-500/10 text-red-300 p-3 flex items-start gap-2 text-sm" data-testid="admin-login-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0"/>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full btn-primary justify-center disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? "Authenticating…" : "Enter Control Room"}
          </button>

          <p className="mt-6 text-xs mono text-zinc-600 text-center">SECURED BY RIDGECX · ZERO-TRUST</p>
        </motion.form>
      </div>

      <style>{`
        .ridge-input {
          width: 100%;
          background: #050505;
          border: 1px solid #27272a;
          color: #fff;
          padding: 12px 14px;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color .2s ease;
          border-radius: 0;
        }
        .ridge-input:focus { border-color: #ff3333; }
        .ridge-input::placeholder { color: #52525b; }
      `}</style>
    </div>
  );
}
